/** True for a file already in our Firebase Storage bucket. */
export function isOwnStorageUrl(url, bucketName) {
  if (typeof url !== 'string' || !bucketName) return false
  let parsed
  try {
    parsed = new URL(url.trim())
  } catch (_) {
    return false
  }
  if (parsed.protocol !== 'https:') return false
  if (parsed.hostname === 'firebasestorage.googleapis.com') {
    return parsed.pathname.startsWith(`/v0/b/${bucketName}/o/`)
  }
  if (parsed.hostname === 'storage.googleapis.com') {
    return parsed.pathname.startsWith(`/${bucketName}/`)
  }
  return false
}

const INLINE_IMAGE = /^data:image\//i

const isPlainObject = (value) =>
  Boolean(value) && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype

export function isInlineImage(value) {
  return typeof value === 'string' && INLINE_IMAGE.test(value.trim())
}

/** Dotted paths of every base64 image left anywhere in a design (or any plain value). */
export function findInlineImages(value, path = '') {
  if (isInlineImage(value)) return [path || '(root)']
  if (Array.isArray(value)) {
    return value.flatMap((item, i) => findInlineImages(item, `${path}[${i}]`))
  }
  if (isPlainObject(value)) {
    return Object.entries(value).flatMap(([key, item]) =>
      findInlineImages(item, path ? `${path}.${key}` : key)
    )
  }
  return []
}

/**
 * A copy with every base64 image replaced by upload(dataUrl), at any depth:
 * front, back, AI base, and each placed graphic. Identical images upload once.
 * Dates, Firestore timestamps, and other non-plain objects are kept as they are.
 */
export async function hostInlineImages(value, upload, cache = new Map()) {
  if (isInlineImage(value)) {
    const key = value.trim()
    if (!cache.has(key)) cache.set(key, Promise.resolve().then(() => upload(key)))
    return cache.get(key)
  }
  if (Array.isArray(value)) {
    const out = []
    for (const item of value) out.push(await hostInlineImages(item, upload, cache))
    return out
  }
  if (isPlainObject(value)) {
    const out = {}
    for (const [key, item] of Object.entries(value)) {
      out[key] = await hostInlineImages(item, upload, cache)
    }
    return out
  }
  return value
}

/**
 * The URL to save for a design image. Files already in our storage are kept as
 * they are: the bucket sends no CORS headers, so the browser cannot download them
 * again. Base64 images are uploaded, and other https links are copied when the
 * browser can read them. Never returns base64.
 */
export async function persistableImageUrl(input, { upload, bucketName }) {
  const value = typeof input === 'string' ? input.trim() : ''
  if (!value) return value
  if (isOwnStorageUrl(value, bucketName)) return value
  if (value.startsWith('data:')) return upload(value)
  if (/^https:\/\//i.test(value)) {
    try {
      return await upload(value)
    } catch (_) {
      return value
    }
  }
  return value
}
